import {
  ICourse,
  IEducation,
  IEmployer,
  ILanguageSkill,
  IPersonCertificate,
  IPersonCompetence,
  IPersonIndustry,
  IPersonRole,
  IPersonSkill,
  IProject,
  IReference,
} from 'types/cvsInterfaces'
import { Translateable } from 'utils/translations'
import { IPerson } from 'types/userInterfaces'
import {
  IItemMissingField,
  IProfileWarningsResult,
  ISectionWarningSummary,
  SectionItemsMap,
  SectionWarningsMap,
  warningSections,
  WarningSection,
} from './types'
import { REQUIRED_WARNING_FIELDS } from './fieldsMap'
import { Translation } from 'types/translation'

interface IBuildProfileWarningsInput {
  personData?: IPerson
  skills: IPersonSkill[]
  industries: IPersonIndustry[]
  roles: IPersonRole[]
  employers: IEmployer[]
  projects: IProject[]
  education: IEducation[]
  courses: ICourse[]
  certificates: IPersonCertificate[]
  competences: IPersonCompetence[]
  references: IReference[]
  languageSkills: ILanguageSkill[]
  activeLanguageCode: string
}

export const isMissingText = (value: unknown): boolean => {
  return typeof value !== 'string' || value.trim().length === 0
}

export const isMissingNumber = (value: unknown): boolean => {
  return typeof value !== 'number' || Number.isNaN(value)
}

export const isMissingDate = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
  }

  if (typeof value === 'string') {
    if (value.trim().length === 0) {
      return true
    }

    return Number.isNaN(new Date(value).getTime())
  }

  return true
}

export const getTranslationForLanguage = <T extends Translateable>(
  translations: T[] | undefined,
  activeLanguageCode: string
): T | undefined => {
  if (!translations || translations.length === 0) {
    return undefined
  }

  const languageMatch = translations.find(translation => translation.Language?.name === activeLanguageCode)
  return languageMatch ?? translations[0]
}

const isMissingId = (value: unknown): boolean => isMissingNumber(value) || Number(value) <= 0

const initSectionWarnings = (): SectionWarningsMap => {
  const entries = warningSections.map(section => {
    const summary: ISectionWarningSummary = {
      section,
      missingFields: 0,
      affectedItems: 0,
    }

    return [section, summary] as const
  })

  return Object.fromEntries(entries) as SectionWarningsMap
}

const initSectionItems = (): SectionItemsMap => {
  const entries = warningSections.map(section => [section, [] as IItemMissingField[]] as const)
  return Object.fromEntries(entries) as SectionItemsMap
}

const pushMissing = (
  itemsBySection: SectionItemsMap,
  section: WarningSection,
  itemId: number | string,
  itemIndex: number,
  missingFields: string[],
  translations?: Translation[]
): IItemMissingField | undefined => {
  if (missingFields.length === 0) {
    return undefined
  }

  const warningItem = {
    section,
    itemId,
    itemIndex,
    missingFields,
    translations,
  }
  itemsBySection[section].push(warningItem)
  return warningItem
}

const buildSkillLikeMissing = (item: IPersonSkill | IPersonIndustry | IPersonRole): string[] => {
  const missingFields: string[] = []
  const required = REQUIRED_WARNING_FIELDS.skills

  if (isMissingId(item.skillId)) {
    missingFields.push(required.skillId)
  }
  if (isMissingNumber(item.level) || Number(item.level) <= 0) {
    missingFields.push(required.level)
  }
  if (isMissingNumber(item.interestLevel)) {
    missingFields.push(required.interestLevel)
  }
  if (isMissingNumber(item.experienceMonths) || Number(item.experienceMonths) <= 0) {
    missingFields.push(required.experienceMonths)
  }
  if (isMissingNumber(item.lastUsed)) {
    missingFields.push(required.lastUsed)
  }

  return missingFields
}

const buildEmployerMissing = (item: IEmployer, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.employers

  if (isMissingDate(item.startDate)) {
    missingFields.push(required.startDate)
  }
  if (isMissingText(translation?.name)) {
    missingFields.push(required.name)
  }
  if (isMissingText(translation?.description)) {
    missingFields.push(required.description)
  }

  return missingFields
}

const buildProjectMissing = (item: IProject, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.projects

  if (isMissingDate(item.startDate)) {
    missingFields.push(required.startDate)
  }
  if (isMissingText(translation?.customerName)) {
    missingFields.push(required.customerName)
  }
  if (isMissingText(translation?.employerName)) {
    missingFields.push(required.employerName)
  }
  if (isMissingText(translation?.name)) {
    missingFields.push(required.name)
  }
  if (isMissingText(translation?.description)) {
    missingFields.push(required.description)
  }

  return missingFields
}

const buildEducationMissing = (item: IEducation, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.education

  if (isMissingDate(item.startDate)) {
    missingFields.push(required.startDate)
  }
  if (isMissingText(item.dateResolution)) {
    missingFields.push(required.dateResolution)
  }
  if (isMissingText(translation?.degree)) {
    missingFields.push(required.degree)
  }
  if (isMissingText(translation?.school)) {
    missingFields.push(required.school)
  }

  return missingFields
}

const buildCourseMissing = (item: ICourse, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.courses

  if (isMissingDate(item.startDate)) {
    missingFields.push(required.startDate)
  }
  if (isMissingText(item.dateResolution)) {
    missingFields.push(required.dateResolution)
  }
  if (isMissingText(translation?.course)) {
    missingFields.push(required.course)
  }
  if (isMissingText(translation?.organizer)) {
    missingFields.push(required.organizer)
  }

  return missingFields
}

const buildCertificateMissing = (item: IPersonCertificate, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.certificates

  if (isMissingDate(item.issueDate)) {
    missingFields.push(required.issueDate)
  }
  if (isMissingText(item.dateResolution)) {
    missingFields.push(required.dateResolution)
  }
  if (isMissingId(item.certificateId)) {
    missingFields.push(required.certificateId)
  }
  if (isMissingText(translation?.certifier)) {
    missingFields.push(required.certifier)
  }

  return missingFields
}

const buildCompetenceMissing = (item: IPersonCompetence, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.competences

  if (isMissingDate(item.issueDate)) {
    missingFields.push(required.issueDate)
  }
  if (isMissingText(item.dateResolution)) {
    missingFields.push(required.dateResolution)
  }
  if (isMissingId(item.competenceId)) {
    missingFields.push(required.competenceId)
  }
  if (isMissingText(translation?.issuer)) {
    missingFields.push(required.issuer)
  }

  return missingFields
}

const buildReferenceMissing = (item: IReference, activeLanguageCode: string): string[] => {
  const missingFields: string[] = []
  const translation = getTranslationForLanguage(item.translations, activeLanguageCode)
  const required = REQUIRED_WARNING_FIELDS.references

  if (isMissingText(item.firstName)) {
    missingFields.push(required.firstName)
  }
  if (isMissingText(item.lastName)) {
    missingFields.push(required.lastName)
  }
  if (isMissingText(item.company)) {
    missingFields.push(required.company)
  }
  if (isMissingText(translation?.letter)) {
    missingFields.push(required.letter)
  }

  return missingFields
}

const buildLanguageMissing = (item: ILanguageSkill): string[] => {
  const missingFields: string[] = []
  const required = REQUIRED_WARNING_FIELDS.languages

  if (isMissingText(item.languageCode)) {
    missingFields.push(required.languageCode)
  }

  if (isMissingNumber(item.level) || Number(item.level) <= 0) {
    missingFields.push(required.level)
  }

  return missingFields
}

export const buildProfileWarnings = ({
  personData,
  skills,
  industries,
  roles,
  employers,
  projects,
  education,
  courses,
  certificates,
  competences,
  references,
  languageSkills,
  activeLanguageCode,
}: IBuildProfileWarningsInput): IProfileWarningsResult => {
  const itemsBySection = initSectionItems()

  const profileMissing: string[] = []
  const requiredProfile = REQUIRED_WARNING_FIELDS.profile
  if (isMissingText(personData?.firstName)) {
    profileMissing.push(requiredProfile.firstName)
  }
  if (isMissingText(personData?.lastName)) {
    profileMissing.push(requiredProfile.lastName)
  }

  pushMissing(itemsBySection, 'profile', personData?.id ?? 'profile', 0, profileMissing)

  skills.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'skills',
      item.id,
      itemIndex,
      buildSkillLikeMissing(item),
      item.Skill?.translations as unknown as Translation[]
    )
  })

  industries.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'industries',
      item.id,
      itemIndex,
      buildSkillLikeMissing(item),
      item.Skill?.translations as unknown as Translation[]
    )
  })

  roles.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'roles',
      item.id,
      itemIndex,
      buildSkillLikeMissing(item),
      item.Skill?.translations as unknown as Translation[]
    )
  })

  employers.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'employers',
      item.id,
      itemIndex,
      buildEmployerMissing(item, activeLanguageCode),
      item.translations as unknown as Translation[]
    )
  })

  projects.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'projects',
      item.id,
      itemIndex,
      buildProjectMissing(item, activeLanguageCode),
      item.translations as unknown as Translation[]
    )
  })

  education.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'education',
      item.id,
      itemIndex,
      buildEducationMissing(item, activeLanguageCode),
      item.translations as unknown as Translation[]
    )
  })

  courses.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'courses',
      item.id,
      itemIndex,
      buildCourseMissing(item, activeLanguageCode),
      item.translations as unknown as Translation[]
    )
  })

  certificates.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'certificates',
      item.id,
      itemIndex,
      buildCertificateMissing(item, activeLanguageCode),
      item.Certificate?.translations as unknown as Translation[]
    )
  })

  competences.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'competences',
      item.id,
      itemIndex,
      buildCompetenceMissing(item, activeLanguageCode),
      item.Competence?.translations as unknown as Translation[]
    )
  })

  references.forEach((item, itemIndex) => {
    pushMissing(
      itemsBySection,
      'references',
      item.id,
      itemIndex,
      buildReferenceMissing(item, activeLanguageCode),
      item.translations as unknown as Translation[]
    )
  })

  languageSkills.forEach((item, itemIndex) => {
    const warningItem = pushMissing(itemsBySection, 'languages', item.id, itemIndex, buildLanguageMissing(item))
    if (warningItem) {
      warningItem.languageCode = item.languageCode
    }
  })

  const sections = initSectionWarnings()
  const items: IItemMissingField[] = []

  warningSections.forEach(section => {
    const sectionItems = itemsBySection[section]
    const missingFields = sectionItems.reduce((total, row) => total + row.missingFields.length, 0)

    sections[section] = {
      section,
      missingFields,
      affectedItems: sectionItems.length,
    }

    items.push(...sectionItems)
  })

  const totalMissingFields = warningSections.reduce((total, section) => total + sections[section].missingFields, 0)

  const sectionsWithWarnings = warningSections.reduce((total, section) => {
    if (sections[section].missingFields > 0) {
      return total + 1
    }

    return total
  }, 0)

  return {
    totalMissingFields,
    sectionsWithWarnings,
    sections,
    items,
    itemsBySection,
  }
}

export const buildEmptyProfileWarnings = (): IProfileWarningsResult => {
  return {
    totalMissingFields: 0,
    sectionsWithWarnings: 0,
    sections: initSectionWarnings(),
    items: [],
    itemsBySection: initSectionItems(),
  }
}
